// Madrid — whitelist-compliant clinics only.
//
// Per safe-competitor-whitelist.md: every non-ACE clinic featured on
// this site must be either (A) a dental unit inside an approved Spanish
// private-hospital group or (B) a location of an approved national
// insurance / low-cost dental chain. Boutique / named-practitioner /
// cosmetic-studio clinics are direct SEO competitors of ACE DNTL and
// never appear here.
//
// VERIFICATION NOTES:
// Entries marked `verified: true` were loaded live and confirmed for
// (brand identity, address, dental-service presence). Entries marked
// `verified: false` have the parent brand on the whitelist but the
// editor could not fully verify the specific page at the time of
// writing — typically because the hospital's website returned 403 or
// 404 to the fetch tool. The editor must spot-check these before
// publication.
//
// Language rule: hospital-integrated clinics are described in
// capability language (dental unit, medically-integrated, insurance-
// oriented). Chain locations are described as accessible-pricing,
// insurance-and-financing-oriented. None of the ACE-lane vocabulary
// (studio, smile-design, craft, signature, framework, author, doctrine)
// is applied to any clinic on this page.

import type { Clinic } from "./clinics-marbella";

export const MADRID_CLINICS: Clinic[] = [
  {
    slug: "hm-nou-delfos-dental",
    name: "HM Hospitales — HM Dental Center (Madrid network)",
    location: "HM Dental services across HM Hospitales Madrid network (HM Sanchinarro, HM Puerta del Sur, HM Madrid, HM Montepríncipe)",
    website: "https://www.hmhospitales.com/",
    verified: false,
    profile:
      "The dental service line of HM Hospitales — one of Spain's larger private-hospital groups — delivered through HM Dental Center and embedded inside HM's Madrid hospital network. Cosmetic, restorative and implant dentistry sit inside a wider medical footprint, with the hospital-group's imaging, surgical and anaesthesia infrastructure available for medically-involved cases.",
    labModel:
      "Hospital-group laboratory arrangements, not positioned as a studio-scale in-house ceramics operation.",
    designWorkflow:
      "Case-dependent. Digital workflows available inside the broader medical protocol rather than foregrounded as an aesthetic signature.",
    prepPhilosophy:
      "Institutional rather than individually-authored. No named preparation doctrine published at the clinic-unit level.",
    ceramistProximity:
      "Not publicly named. Readers who weight ceramist-proximity should ask directly.",
    internationalHandling:
      "Strong. HM Hospitales operates a structured international-patient service at group level with multilingual intake.",
    reviewProfile:
      "Aggregated across the HM hospital estate rather than isolated to the dental unit. Volume is substantial.",
    leadsOn: [
      "Medically-integrated dental care inside one of Madrid's largest private-hospital networks",
      "Hospital-group imaging, anaesthesia and surgical infrastructure on site for complex or medically-involved cases",
      "Structured insurance billing across the main Spanish private-health policies",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Authored aesthetic preparation philosophy",
    ],
  },
  {
    slug: "ruber-internacional-dental",
    name: "Hospital Ruber Internacional — dental service",
    location: "Calle de La Masó, 38, 28034 Madrid",
    website: "https://www.ruberinternacional.es/",
    verified: false,
    profile:
      "The dental service inside Hospital Ruber Internacional — a full-service private hospital in northern Madrid with a long institutional tenure and a strong international-patient history. Dentistry sits inside a hospital footprint with adjacent medical, surgical and imaging support.",
    labModel:
      "Hospital-affiliated laboratory arrangements. The ceramist relationship is not publicly foregrounded.",
    designWorkflow:
      "Multidisciplinary planning inside the hospital context. Digital tooling available but not positioned as a defining aesthetic signature.",
    prepPhilosophy:
      "Institutional. No named individual-clinician preparation doctrine published at the dental-unit level.",
    ceramistProximity: "Not publicly detailed.",
    internationalHandling:
      "Very strong. Ruber Internacional operates a long-standing international-patient service with multilingual intake.",
    reviewProfile:
      "Aggregated at the hospital level. Dental-specific depth harder to extract.",
    leadsOn: [
      "Medically-integrated dental care inside an established Madrid private hospital",
      "Long international-patient tenure",
      "Hospital-scale support services for complex cases",
    ],
    trailsOn: [
      "Public ceramist-level transparency",
      "Published single-clinician preparation philosophy",
    ],
  },
  {
    slug: "sanitas-milenium-castello",
    name: "Sanitas Dental — Milenium Castelló (Madrid)",
    location: "Calle Castelló, 60, 28001 Madrid",
    website: "https://www.sanitas.es/dental/clinicas-dentales-milenium",
    verified: true,
    profile:
      "A Madrid location of Sanitas Dental's Milenium network — the insurance-branded flagship dental-clinic line operated by Sanitas (part of Bupa). The Castelló clinic sits in the Salamanca district and represents one of the network's central Madrid anchors. Pricing, plan coverage and financing are structured at the national-brand level.",
    labModel:
      "Chain-standardised lab routing. The ceramist relationship is not foregrounded at the clinic-location level; material and lab choices follow Sanitas network protocols.",
    designWorkflow:
      "Protocolised aesthetic workflow at chain scale. Digital scanning and design tools available per network standard rather than as a named studio signature.",
    prepPhilosophy:
      "Network-standardised. No individual-clinician preparation doctrine published at the location level.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Standard Sanitas-network intake. Primarily a domestic insurance-and-financing proposition rather than an international-patient dental-tourism operation.",
    reviewProfile:
      "Aggregated across the Sanitas-Milenium network. Volume is very high; depth skews to routine and insurance-covered treatment rather than aesthetic-case narratives.",
    leadsOn: [
      "National insurance-branded network location with accessible pricing and financing plans",
      "Coverage and billing integration with the main Sanitas private-health policies",
      "Extended-hours, weekend and network-wide emergency availability",
    ],
    trailsOn: [
      "Named-ceramist or single-clinician-craft transparency",
      "Authored aesthetic preparation doctrine",
    ],
  },
  {
    slug: "vitaldent-madrid",
    name: "Vitaldent — Madrid (national chain)",
    location: "Multiple Vitaldent locations across Madrid (61 clinics citywide)",
    website: "https://clinicas.vitaldent.com/madrid",
    verified: false,
    profile:
      "A Madrid location of Vitaldent — Spain's largest national low-cost dental chain, with more than four hundred clinics countrywide and more than sixty inside the Madrid metropolitan area. Positioning is price-accessible with in-house financing plans, not aesthetic-studio-scale craft.",
    labModel:
      "Chain-standardised lab arrangements. The ceramist is not individually named at clinic-location level.",
    designWorkflow:
      "Protocolised. Digital tools deployed according to chain standards rather than case-by-case aesthetic authorship.",
    prepPhilosophy:
      "Network-standardised. No individual preparation doctrine published.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Network intake available in standard languages. Primarily a domestic accessible-pricing proposition.",
    reviewProfile:
      "Very high volume across the national network. Depth varies widely clinic-by-clinic; reviews centre on pricing and financing rather than aesthetic case narratives.",
    leadsOn: [
      "National low-cost dental chain with in-house financing and monthly payment plans",
      "Dense Madrid footprint with walk-in access across most districts",
      "Straightforward pricing structure for routine and restorative dentistry",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Single-clinician craft positioning",
    ],
  },
  {
    slug: "sanitas-milenium-general-oraa",
    name: "Sanitas Dental — Milenium General Oraá (Madrid)",
    location: "Calle General Oraá, 73, 28006 Madrid",
    website: "https://www.sanitas.es/dental/clinicas-dentales-milenium",
    verified: true,
    profile:
      "A second Madrid anchor of Sanitas Dental's Milenium network, in the Salamanca district. The Milenium network is Sanitas's insurance-branded flagship dental-clinic line (Sanitas is part of Bupa). The General Oraá location serves the residential east-central Madrid catchment.",
    labModel:
      "Chain-standardised lab routing. Ceramist relationship not publicly foregrounded at clinic-location level.",
    designWorkflow:
      "Protocolised at network level. Digital tools available per Sanitas-Milenium standards.",
    prepPhilosophy:
      "Network-standardised.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Standard Sanitas intake. Domestic insurance-coverage-oriented.",
    reviewProfile:
      "Aggregated at network level.",
    leadsOn: [
      "Insurance-and-financing-oriented pricing inside the Sanitas private-health ecosystem",
      "Extended-hours availability consistent across the Milenium network",
      "Coverage and billing integration with the main Spanish dental-insurance products",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Studio-scale single-clinician aesthetic authorship",
    ],
  },
];
